import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';

/** Gameplay screens that require the native app. On web, navigating to one of
 *  these opens the "download the app to play" gate instead. */
const PLAY_SCREENS: ReadonlySet<ScreenName> = new Set([
  'matching',
  'match',
  'room',
  'bossSelect',
  'bossBattle',
]);

function isNative(): boolean {
  // Native-target dev builds run in a normal browser while we validate mobile
  // layouts. Honor the explicit build target so Room/Match can be tested at
  // device viewports without weakening the regular web production gate.
  if (import.meta.env.VITE_TARGET === 'native') return true;
  try {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android';
  } catch {
    return false;
  }
}

export type ScreenName =
  | 'login'
  | 'home'
  | 'matching'
  | 'match'
  | 'result'
  | 'leaderboard'
  | 'settings'
  | 'shop'
  | 'inventory'
  | 'friends'
  | 'clan'
  | 'room'
  | 'bossSelect'
  | 'bossBattle';

export type MatchResultKind = 'win' | 'lose';

interface UIState {
  screen: ScreenName;
  matchResult: MatchResultKind;
  showScanlines: boolean;
  /** Screen to return to when pressing back from a sub-screen (e.g. shop
   * opened from room). Consumed + cleared by the destination screen. */
  returnTo: ScreenName | null;
  /** True when a web user tried to enter gameplay → show the download gate. */
  playGateOpen: boolean;
  go: (screen: ScreenName, payload?: unknown) => void;
  closePlayGate: () => void;
  setReturnTo: (screen: ScreenName | null) => void;
  setShowScanlines: (v: boolean) => void;
}

export const useUIStore = create<UIState>((set) => ({
  screen: 'login',
  matchResult: 'win',
  showScanlines: true,
  returnTo: null,
  playGateOpen: false,
  go: (screen, payload) =>
    set((state) => {
      // Web can't play matches — push users to the native app instead of
      // navigating into the gameplay flow.
      if (!isNative() && PLAY_SCREENS.has(screen)) {
        return { playGateOpen: true };
      }
      if (screen === 'result' && (payload === 'win' || payload === 'lose')) {
        return { screen, matchResult: payload };
      }
      return { screen, matchResult: state.matchResult };
    }),
  closePlayGate: () => set({ playGateOpen: false }),
  setReturnTo: (screen) => set({ returnTo: screen }),
  setShowScanlines: (v) => set({ showScanlines: v }),
}));
